import React from "react";
import { Box, Paper, Typography, Alert } from "@mui/material";

const AzureGuide = () => (
  <Box sx={{ mt: 2, px: 1 }}>
    {/* Step 1 */}
    <Paper
      elevation={1}
      sx={{ p: 2, mb: 2, bgcolor: "background.paper", borderRadius: 2 }}
    >
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Step 1: Log in to Azure Portal
      </Typography>
      <Typography variant="body2" gutterBottom>
        Go to{" "}
        <a
          href="https://portal.azure.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://portal.azure.com
        </a>{" "}
        and sign in with your Azure account.
      </Typography>
    </Paper>

    {/* Step 2 */}
    <Paper
      elevation={1}
      sx={{ p: 2, mb: 2, bgcolor: "background.paper", borderRadius: 2 }}
    >
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Step 2: Register an Application
      </Typography>
      <Typography variant="body2" gutterBottom component="div">
        <ol>
          <li>
            Navigate to{" "}
            <strong>Azure Active Directory &gt; App registrations</strong>.
          </li>
          <li>
            Click <strong>+ New registration</strong>.
          </li>
          <li>
            Set:
            <ul>
              <li>
                <strong>Name</strong>: Regodit
              </li>
              <li>
                <strong>Supported account types</strong>: Single tenant
              </li>
              <li>
                <strong>Redirect URI</strong>: Leave blank
              </li>
            </ul>
          </li>
          <li>
            Click <strong>Register</strong>.
          </li>
        </ol>
      </Typography>
    </Paper>

    {/* Step 3 */}
    <Paper
      elevation={1}
      sx={{ p: 2, mb: 2, bgcolor: "background.paper", borderRadius: 2 }}
    >
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Step 3: Capture App Details
      </Typography>
      <Typography variant="body2" gutterBottom>
        From the app overview, copy:
      </Typography>
      <ul>
        <li>
          <strong>Application (client) ID</strong> → client_id
        </li>
        <li>
          <strong>Directory (tenant) ID</strong> → tenant_id
        </li>
      </ul>
    </Paper>

    {/* Step 4 */}
    <Paper
      elevation={1}
      sx={{ p: 2, mb: 2, bgcolor: "background.paper", borderRadius: 2 }}
    >
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Step 4: Add API Permissions
      </Typography>
      <Typography variant="body2" gutterBottom component="div">
        <ol>
          <li>
            Go to <strong>API permissions &gt; + Add a permission</strong>.
          </li>
          <li>
            Choose <strong>Azure Service Management</strong>. Under{" "}
            <strong>Delegated permissions</strong>, select{" "}
            <strong>user_impersonation</strong>.
          </li>
          <li>
            Next, choose <strong>Microsoft Graph</strong> →{" "}
            <strong>Application permissions</strong> → select{" "}
            <strong>User.Read.All</strong> and{" "}
            <strong>Directory.Read.All</strong>.
          </li>
          <li>
            Click <strong>Add permissions</strong>.
          </li>
          <li>
            Now click the button{" "}
            <strong>✔️ Grant admin consent for &lt;Your Tenant Name&gt;</strong>
            . When prompted with "Are you sure you want to grant admin
            consent?", click <strong>Yes</strong>.
          </li>
          <li>
            The permissions status will show a green tick (✓) in the permissions
            table.
          </li>
        </ol>
      </Typography>
    </Paper>

    {/* Step 5 */}
    <Paper
      elevation={1}
      sx={{ p: 2, mb: 2, bgcolor: "background.paper", borderRadius: 2 }}
    >
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Step 5: Create a Client Secret
      </Typography>
      <Typography variant="body2" gutterBottom component="div">
        <ol>
          <li>
            Go to <strong>Certificates & secrets &gt; Client secrets</strong>.
          </li>
          <li>
            Click <strong>+ New client secret</strong>.
          </li>
          <li>Add a description (e.g., "Regodit Access") and set expiry.</li>
          <li>
            Click <strong>Add</strong> and copy the secret shown (visible only
            once).
          </li>
        </ol>
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "medium" }}>
        → This is your <strong>client_secret</strong>.
      </Typography>
    </Paper>

    {/* Step 6: Assign Azure Role */}
    <Paper
      elevation={1}
      sx={{ p: 2, mb: 2, bgcolor: "background.paper", borderRadius: 2 }}
    >
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Step 6: Assign Azure Role
      </Typography>
      <Typography variant="body2" gutterBottom component="div">
        <ol>
          <li>
            Go to <strong>Subscriptions</strong>, select your subscription on the Azure Portal and then copy the <strong>subscription ID</strong> which you want to integrate with.
          </li>
          <li>
            Click{" "}
            <strong>Access Control (IAM) &gt; + Add role assignment</strong>.
          </li>
          <li>
            Choose:
            <ul>
              <li>
                <strong>Role</strong>: Reader (or Reader and Data Access if
                needed)
              </li>
              <li>
                <strong>Assign access to</strong>: User, group, or service
                principal
              </li>
              <li>
                <strong>Select</strong> the Regodit app as member
              </li>
            </ul>
          </li>
          <li>
            Click <strong>Next &gt; Review + assign</strong>.
          </li>
        </ol>
      </Typography>
    </Paper>

    {/* Step 7: Extract and Submit Credentials */}
    <Paper
      elevation={1}
      sx={{ p: 2, bgcolor: "background.paper", borderRadius: 2 }}
    >
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Step 7: Extract and Submit Credentials
      </Typography>
      <Typography variant="body2" gutterBottom>
        Prepare the credentials in the format given below:
      </Typography>

      <Alert severity="info" sx={{ mt: 1 }}>
        Keep your credentials file private. Do not share it with others.
      </Alert>
    </Paper>
  </Box>
);

export default AzureGuide;


